import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import RelPill from "../RelPill";
import { useT } from "../../context/LanguageContext";
import {
  ChevronLeft, Trophy, CalendarDays, Users, Newspaper, MessageSquare, Send, Crown, Medal,
} from "lucide-react";

/**
 * V2 / League Hub — sub-page that lives inside MobileShell so the bottom nav
 * (My League / Explore / Social / Profile) stays visible. 5 inline tabs:
 *   Standings · Fixtures · Players · Feed · Chat
 *
 * Reuses the existing v1 endpoints unchanged.
 */
const TAB_KEYS = ["standings", "fixtures", "players", "feed", "chat"];
const TAB_ICONS = { standings: Trophy, fixtures: CalendarDays, players: Users, feed: Newspaper, chat: MessageSquare };

export default function LeagueHubV2() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useT();
  const TABS = TAB_KEYS.map((id) => ({ id, label: t(`v2.hub.tab.${id}`), Icon: TAB_ICONS[id] }));
  const [league, setLeague] = useState(null);
  // Honour ?tab=chat (or any TAB_KEYS value) so deep-links from the inbox
  // open directly on that sub-tab. Falls back to "standings".
  const initialTab = TAB_KEYS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "standings";
  const [tab, setTab] = useState(initialTab);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${id}`);
        if (!cancelled) setLeague(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || t("v2.hub.error"));
      }
    })();
    return () => { cancelled = true; };
  }, [id, t]);

  if (error) {
    return (
      <div className="px-5 pt-5" data-testid="v2-hub-error">
        <BackLink t={t} />
        <div className="mt-4 bg-[#12121C] border border-[#EF4444]/30 rounded-2xl p-4 text-sm text-[#EF4444]">{error}</div>
      </div>
    );
  }
  if (!league) {
    return <div className="p-6 text-center text-[#52525B] text-sm" data-testid="v2-hub-loading">{t("v2.shell.loading")}</div>;
  }

  const isMember = user && user !== false && (
    user.role === "admin" || (league.players || []).includes(user.id)
  );
  const visibleTabs = TABS.filter((tab) => (tab.id === "feed" || tab.id === "chat") ? isMember : true);
  const pct = Math.min(100, Math.round((league.player_count / (league.capacity || 20)) * 100));

  return (
    <div className="flex flex-col" data-testid="v2-hub">
      {/* Header */}
      <div className="px-5 pt-4 pb-3" data-testid="v2-hub-header">
        <BackLink t={t} />
        <div className="mt-3 flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{league.platform || "mobile"}</div>
            <h1 className="text-xl font-bold text-white mt-0.5 truncate">{league.name}</h1>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[#7C3AED]/20 text-[#A5B4FC] flex-shrink-0">{league.status}</span>
        </div>
        <div className="text-[11px] text-[#71717A] mt-1">{t("v2.hub.fillPct", { n: league.player_count, m: league.capacity || 20, p: pct })}</div>
        <div className="h-1 bg-[#1F1F2A] rounded-full overflow-hidden mt-2">
          <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#3B82F6]" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Sticky tab strip */}
      <div className="sticky top-0 z-10 bg-[#070710]/95 backdrop-blur-md border-y border-[#1F1F2A] px-3 py-2 -mx-px" data-testid="v2-hub-tabs">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {visibleTabs.map(({ id: tid, label, Icon }) => {
            const active = tab === tid;
            return (
              <button
                key={tid}
                onClick={() => setTab(tid)}
                data-testid={`v2-hub-tab-${tid}`}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                  active
                    ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/20"
                    : "text-[#71717A] hover:text-[#A1A1AA]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      <div className="px-5 pt-4 pb-4">
        {tab === "standings" && <StandingsView leagueId={league.id} viewerId={user?.id} t={t} />}
        {tab === "fixtures" && <FixturesView leagueId={league.id} viewerId={user?.id} status={league.status} t={t} />}
        {tab === "players" && <PlayersView league={league} viewer={user} t={t} />}
        {tab === "feed" && isMember && <FeedView leagueId={league.id} t={t} />}
        {tab === "chat" && isMember && <ChatView leagueId={league.id} viewer={user} t={t} />}
      </div>
    </div>
  );
}

function BackLink({ t }) {
  return (
    <Link
      to="/v2"
      data-testid="v2-hub-back"
      className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.2em] text-[#71717A] hover:text-white transition-colors"
    >
      <ChevronLeft className="w-3.5 h-3.5" /> {t("v2.hub.back")}
    </Link>
  );
}

// ============================== STANDINGS ==============================
function StandingsView({ leagueId, viewerId, t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/standings`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setRows([]); }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  if (rows === null) return <Skeleton t={t} />;
  if (!rows.length) return <Empty text={t("v2.hub.standings.empty")} />;

  return (
    <div className="space-y-1.5" data-testid="v2-hub-standings">
      {/* Header row */}
      <div className="grid grid-cols-[28px_1fr_28px_28px_36px] gap-1 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-[#52525B]">
        <span>#</span>
        <span>Player</span>
        <span className="text-center">P</span>
        <span className="text-center">GD</span>
        <span className="text-center">PTS</span>
      </div>
      {rows.map((r, i) => {
        const rank = r.rank || (i + 1);
        const isMe = viewerId && r.user_id === viewerId;
        const RankBadge = rank === 1 ? Crown : rank === 2 ? Trophy : rank === 3 ? Medal : null;
        const rankColor = rank === 1 ? "#FFD700" : rank === 2 ? "#C0C0C0" : rank === 3 ? "#CD7F32" : "#71717A";
        return (
          <div
            key={r.user_id}
            data-testid={`v2-hub-st-row-${r.user_id}`}
            className={`grid grid-cols-[28px_1fr_28px_28px_36px] gap-1 items-center px-3 py-2 rounded-xl border ${
              isMe ? "bg-[#7C3AED]/15 border-[#7C3AED]/40" : "bg-[#12121C] border-[#1F1F2A]"
            }`}
          >
            <span className="flex items-center justify-start">
              {RankBadge ? <RankBadge className="w-3.5 h-3.5" style={{ color: rankColor }} /> : <span className="text-[11px] text-[#71717A] font-bold">{rank}</span>}
            </span>
            <Link to={`/v2/players/${r.username}`} className="min-w-0 flex items-center gap-1.5">
              <span className="text-sm font-semibold text-white truncate">{r.username}</span>
              {r.reliability != null && <RelPill score={r.reliability} flagged={r.reliability_flagged} />}
            </Link>
            <span className="text-center text-xs text-[#A1A1AA] tabular-nums">{r.played ?? 0}</span>
            <span className="text-center text-xs tabular-nums" style={{ color: (r.gd || 0) > 0 ? "#10B981" : (r.gd || 0) < 0 ? "#EF4444" : "#A1A1AA" }}>
              {(r.gd || 0) > 0 ? `+${r.gd}` : (r.gd ?? 0)}
            </span>
            <span className="text-center text-sm font-bold text-white tabular-nums">{r.points ?? 0}</span>
          </div>
        );
      })}
    </div>
  );
}

// ============================== FIXTURES ==============================
function FixturesView({ leagueId, viewerId, status, t }) {
  const [matches, setMatches] = useState(null);
  const scrollRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/fixtures`);
        if (!cancelled) setMatches(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setMatches([]); }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  // Group by date (calendar view). Sort dates ascending so today / soonest
  // appears first. Fixtures missing a scheduled_at fall into a "TBD" bucket
  // at the bottom so they're never lost.
  const grouped = useMemo(() => {
    const out = new Map();
    for (const m of (matches || [])) {
      const k = m.scheduled_at || "TBD";
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(m);
    }
    return Array.from(out.entries()).sort((a, b) => {
      if (a[0] === "TBD") return 1;
      if (b[0] === "TBD") return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [matches]);

  const todayStr = new Date().toISOString().slice(0, 10);

  // After fixtures load, auto-scroll the calendar to today's date (or the
  // nearest upcoming day if today has no fixtures) so the player doesn't
  // have to manually scroll past past matchdays. Runs once per dataset.
  useEffect(() => {
    if (!matches || matches.length === 0) return;
    const days = grouped.map(([d]) => d).filter((d) => d !== "TBD");
    if (!days.length) return;
    const target =
      days.find((d) => d === todayStr) ||
      days.find((d) => d > todayStr) ||
      days[days.length - 1];
    // Wait for the DOM nodes to mount, then scroll smoothly.
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="v2-cal-day-${target}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [matches, grouped, todayStr]);

  if (matches === null) return <Skeleton t={t} />;
  if (!matches.length) {
    return <Empty text={status === "open" ? t("v2.hub.fixtures.openEmpty") : t("v2.hub.fixtures.empty")} />;
  }

  return (
    <div ref={scrollRef} className="space-y-4" data-testid="v2-hub-fixtures">
      {grouped.map(([day, list]) => (
        <CalendarDay
          key={day}
          day={day}
          matches={list}
          viewerId={viewerId}
          todayStr={todayStr}
          t={t}
        />
      ))}
    </div>
  );
}

/** Calendar-style date header. Shows a chunky weekday + day-of-month tile on
 *  the left and a thin separator with the relative-date label, followed by
 *  the day's fixtures stacked underneath. Highlights TODAY in purple. */
function CalendarDay({ day, matches, viewerId, todayStr, t }) {
  const isTBD = day === "TBD";
  const isToday = !isTBD && day === todayStr;
  const isPast = !isTBD && day < todayStr;

  let weekday = "", monthDay = "", relative = "";
  if (!isTBD) {
    const d = new Date(`${day}T00:00:00`);
    if (!isNaN(d.getTime())) {
      weekday = d.toLocaleDateString(undefined, { weekday: "short" });
      monthDay = d.toLocaleDateString(undefined, { month: "short", day: "2-digit" });
      const diffDays = Math.round((d.getTime() - new Date(todayStr + "T00:00:00").getTime()) / 86400000);
      if (diffDays === 0) relative = t("v2.cal.today");
      else if (diffDays === 1) relative = t("v2.cal.tomorrow");
      else if (diffDays === -1) relative = t("v2.cal.yesterday");
      else if (diffDays > 1 && diffDays <= 7) relative = t("v2.cal.inDays", { n: diffDays });
      else if (diffDays < -1 && diffDays >= -7) relative = t("v2.cal.daysAgo", { n: Math.abs(diffDays) });
    }
  }

  const tileBg = isToday ? "bg-[#7C3AED]" : isPast ? "bg-[#1F1F2A]" : "bg-[#12121C] border border-[#27272F]";
  const tileText = isToday ? "text-white" : isPast ? "text-[#71717A]" : "text-white";

  return (
    <section data-testid={`v2-cal-day-${day}`} data-today={isToday}>
      <header className="flex items-end gap-3 mb-2 px-1">
        <div className={`rounded-2xl px-3 py-2 min-w-[60px] text-center shadow-md ${tileBg} ${tileText}`}>
          {isTBD ? (
            <>
              <div className="text-[9px] font-bold uppercase tracking-[0.25em] opacity-70">{t("v2.cal.tbd.line1")}</div>
              <div className="text-base font-bold leading-tight mt-0.5">{t("v2.cal.tbd.line2")}</div>
            </>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] opacity-80">{weekday}</div>
              <div className="text-lg font-bold leading-tight mt-0.5">{monthDay}</div>
            </>
          )}
        </div>
        <div className="flex-1 pb-1">
          {!isTBD && relative && (
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">{relative}</div>
          )}
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#52525B]">
            {t("v2.cal.matchCount", { n: matches.length })}
          </div>
        </div>
      </header>
      <div className="space-y-1.5 ml-[68px]">
        {matches.map((m) => <FixtureRow key={m.id} m={m} viewerId={viewerId} t={t} />)}
      </div>
    </section>
  );
}

function FixtureRow({ m, viewerId, t }) {
  const isInvolved = viewerId && (m.home_id === viewerId || m.away_id === viewerId);
  const home = m.home?.username || "?";
  const away = m.away?.username || "?";
  const isConfirmed = m.status === "confirmed";
  const hs = m.confirmed_score_home;
  const as_ = m.confirmed_score_away;
  return (
    <Link
      to={`/v2/matches/${m.id}`}
      data-testid={`v2-hub-fix-${m.id}`}
      className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 transition-colors border ${
        isInvolved ? "bg-[#7C3AED]/10 border-[#7C3AED]/30 hover:bg-[#7C3AED]/15" : "bg-[#12121C] border-[#1F1F2A] hover:bg-[#161620]"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white truncate">
          <span className={m.home_id === viewerId ? "font-bold" : ""}>{home}</span>
          <span className="text-[#52525B] mx-1.5">vs</span>
          <span className={m.away_id === viewerId ? "font-bold" : ""}>{away}</span>
        </div>
        <div className="text-[10px] text-[#71717A] mt-0.5">
          {m.round != null ? `R${m.round + 1} · ` : ""}{(m.status || "scheduled").replace(/_/g, " ")}
        </div>
      </div>
      {isConfirmed ? (
        <span className="text-sm font-bold text-white tabular-nums flex-shrink-0">{hs}–{as_}</span>
      ) : (
        <span className="text-[10px] uppercase tracking-wider text-[#52525B] flex-shrink-0">{t("v2.hub.openTag")}</span>
      )}
    </Link>
  );
}

// ============================== PLAYERS ==============================
function PlayersView({ league, viewer, t }) {
  const list = league.players_detail || [];
  if (!list.length) return <Empty text={t("v2.hub.players.empty")} />;
  return (
    <div className="space-y-1.5" data-testid="v2-hub-players">
      {list.map((p, i) => (
        <div key={p.id} className="flex items-center gap-3 bg-[#12121C] border border-[#1F1F2A] rounded-2xl px-3 py-2.5" data-testid={`v2-hub-player-${p.id}`}>
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
            {(p.username || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/v2/players/${p.username}`} className="text-sm font-semibold text-white hover:text-[#A5B4FC] truncate block">
              {p.username}
              {viewer && p.id === viewer.id && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-[#10B981]">{t("v2.hub.players.you")}</span>}
            </Link>
            <div className="text-[10px] text-[#71717A] truncate font-mono">#{String(i + 1).padStart(2, "0")} · {p.game_id || "—"}</div>
          </div>
          {viewer && viewer !== false && p.id !== viewer.id && (
            <Link
              to={`/chat/${p.id}`}
              data-testid={`v2-hub-dm-${p.id}`}
              className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-lg bg-[#7C3AED]/15 text-[#A5B4FC] border border-[#7C3AED]/30 hover:bg-[#7C3AED]/25 flex-shrink-0"
            >
              {t("v2.hub.players.dm")}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================== FEED ==============================
function FeedView({ leagueId, t }) {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/feed`);
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setPosts([]); }
    })();
    return () => { cancelled = true; };
  }, [leagueId]);

  if (posts === null) return <Skeleton t={t} />;
  if (!posts.length) return <Empty text={t("v2.hub.feed.empty")} />;

  return (
    <div className="space-y-2" data-testid="v2-hub-feed">
      {posts.slice(0, 50).map((p) => (
        <article key={p.id} data-testid={`v2-hub-feed-${p.id}`} className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl p-3">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-white text-xs">
              {(p.username || "?").charAt(0).toUpperCase()}
            </span>
            <span className="text-xs font-semibold text-white truncate">{p.username || "ePlay"}</span>
            <span className="text-[10px] text-[#52525B] ml-auto">{relTime(p.created_at)}</span>
          </div>
          {p.text && <p className="text-sm text-white/90 mt-2 whitespace-pre-wrap break-words leading-relaxed">{p.text}</p>}
        </article>
      ))}
    </div>
  );
}

// ============================== CHAT ==============================
function ChatView({ leagueId, viewer, t }) {
  const [messages, setMessages] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/chat`);
        if (!cancelled) setMessages(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setMessages([]); }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, 8000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [leagueId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/chat`, { text });
      setMessages((prev) => [...(prev || []), data]);
      setDraft("");
    } catch { /* noop */ }
    finally { setSending(false); }
  };

  if (messages === null) return <Skeleton t={t} />;

  return (
    <div className="flex flex-col" data-testid="v2-hub-chat" style={{ minHeight: "60vh" }}>
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto pb-3 max-h-[60vh]">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-[#52525B] py-8">{t("v2.hub.chat.empty")}</div>
        ) : messages.map((m) => {
          const mine = viewer && m.user_id === viewer.id;
          return (
            <div key={m.id} data-testid={`v2-hub-chat-msg-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white" : "bg-[#12121C] border border-[#1F1F2A] text-white"}`}>
                {!mine && <div className="text-[10px] font-bold text-[#A5B4FC] mb-0.5">{m.username}</div>}
                <div className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.text}</div>
                <div className={`text-[9px] mt-0.5 ${mine ? "text-white/70" : "text-[#52525B]"}`}>{relTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      <form onSubmit={send} className="flex items-center gap-2 pt-2 border-t border-[#1F1F2A]" data-testid="v2-hub-chat-form">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("v2.hub.chat.placeholder")}
          data-testid="v2-hub-chat-input"
          maxLength={2000}
          className="flex-1 bg-[#0F0F18] border border-[#1F1F2A] rounded-2xl px-3 py-2.5 text-sm text-white placeholder-[#52525B] focus:border-[#7C3AED] outline-none"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          data-testid="v2-hub-chat-send"
          className="p-2.5 rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

// ============================== HELPERS ==============================
function Skeleton({ t }) {
  return <div className="py-10 text-center text-sm text-[#52525B]" data-testid="v2-hub-loading-tab">{t ? t("v2.shell.loading") : "…"}</div>;
}

function Empty({ text }) {
  return (
    <div className="bg-[#12121C] border border-dashed border-[#1F1F2A] rounded-2xl p-6 text-center text-sm text-[#71717A]" data-testid="v2-hub-empty">
      {text}
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
